(() => {
  class AspadzProductStoryCarousel extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.retryTimer = null;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.handleMotionPreferenceChange = this.handleMotionPreferenceChange.bind(this);
    }

    connectedCallback() {
      this.addMotionListener();
      this.init();
    }

    disconnectedCallback() {
      window.clearTimeout(this.retryTimer);
      this.removeMotionListener();

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }

    addMotionListener() {
      if (this.motionQuery.addEventListener) {
        this.motionQuery.addEventListener('change', this.handleMotionPreferenceChange);
      } else {
        this.motionQuery.addListener(this.handleMotionPreferenceChange);
      }
    }

    removeMotionListener() {
      if (this.motionQuery.removeEventListener) {
        this.motionQuery.removeEventListener('change', this.handleMotionPreferenceChange);
      } else {
        this.motionQuery.removeListener(this.handleMotionPreferenceChange);
      }
    }

    init() {
      if (this.swiper) return;

      const slider = this.querySelector('[data-aspadz-product-story-carousel-slider]');
      const slides = this.querySelectorAll('.aspadz-product-story-carousel__slide');
      if (!slider || slides.length < 2) {
        this.updateActiveCopy(slides[0]);
        this.handleMotionPreferenceChange();
        return;
      }

      if (typeof window.Swiper === 'undefined') {
        this.retryTimer = window.setTimeout(() => this.init(), 120);
        return;
      }

      const delay = Number(this.dataset.autoplayDelay) || 5000;
      const pagination = this.querySelector('[data-aspadz-product-story-carousel-pagination]');
      this.style.setProperty('--aspadz-psc-delay', `${delay}ms`);

      this.swiper = new window.Swiper(slider, {
        allowTouchMove: true,
        autoplay: this.motionQuery.matches
          ? false
          : {
              delay,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            },
        centeredSlides: true,
        loop: true,
        loopAdditionalSlides: slides.length,
        navigation: {
          nextEl: this.querySelector('[data-aspadz-product-story-carousel-next]'),
          prevEl: this.querySelector('[data-aspadz-product-story-carousel-prev]'),
        },
        pagination: pagination
          ? {
              clickable: true,
              el: pagination,
              renderBullet(index, className) {
                return `
                  <button class="${className}" type="button" aria-label="Go to slide ${index + 1}">
                    <span class="aspadz-product-story-carousel__pagination-track">
                      <span class="aspadz-product-story-carousel__pagination-progress"></span>
                    </span>
                  </button>
                `;
              },
            }
          : undefined,
        slidesPerView: 'auto',
        spaceBetween: 16,
        speed: 650,
        watchOverflow: true,
        breakpoints: {
          750: {
            spaceBetween: 20,
          },
        },
        on: {
          init: (swiper) => {
            this.updateActiveCopy(swiper);
            this.restartProgress();
          },
          slideChangeTransitionStart: (swiper) => {
            this.updateActiveCopy(swiper);
            this.restartProgress();
          },
          autoplayResume: () => this.restartProgress(),
        },
      });

      this.handleMotionPreferenceChange();
    }

    updateActiveCopy(source) {
      const activeSlide = source?.slides ? source.slides[source.activeIndex] : source;
      if (!activeSlide) return;

      const title = activeSlide.querySelector('[data-aspadz-product-story-carousel-copy-title]')?.textContent.trim() || '';
      const description = activeSlide.querySelector('[data-aspadz-product-story-carousel-copy-description]')?.textContent.trim() || '';

      this.querySelectorAll('[data-aspadz-product-story-carousel-active-title]').forEach((item) => {
        item.textContent = title;
      });

      this.querySelectorAll('[data-aspadz-product-story-carousel-active-description]').forEach((item) => {
        item.textContent = description;
      });
    }

    restartProgress() {
      this.classList.remove('is-progressing');

      window.requestAnimationFrame(() => {
        if (!this.motionQuery.matches) {
          this.classList.add('is-progressing');
        }
      });
    }

    handleMotionPreferenceChange() {
      const reduceMotion = this.motionQuery.matches;
      this.classList.toggle('aspadz-product-story-carousel--reduced-motion', reduceMotion);

      if (!this.swiper?.autoplay) {
        this.classList.toggle('is-progressing', !reduceMotion);
        return;
      }

      if (reduceMotion) {
        this.swiper.autoplay.stop();
        this.classList.remove('is-progressing');
      } else {
        this.swiper.autoplay.start();
        this.restartProgress();
      }
    }
  }

  if (!window.customElements.get('aspadz-product-story-carousel')) {
    window.customElements.define('aspadz-product-story-carousel', AspadzProductStoryCarousel);
  }
})();
